import { useState, useRef } from "react";
import "./SecuritySection.scss";

const SecuritySection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const touchStartTime = useRef<number | null>(null);

  const securityFeatures = [
    {
      title: "Blockchain Security",
      description: "Immutable record keeping with distributed ledger technology ensuring complete transparency.",
      image: "/LandingPageImages/BlockchainSecurity.svg",
    },
    {
      title: "AI Proctoring",
      description: "Real-time monitoring with artificial intelligence to detect any suspicious activities.",
      image: "/LandingPageImages/AIProctoring.png",
    },
    {
      title: "Facial Recognition",
      description: "Advanced facial verification for secure and seamless identity authentication.",
      image: "/LandingPageImages/FacialRecognition.png",
    },
  ];

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % securityFeatures.length);
  };

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + securityFeatures.length) % securityFeatures.length);
  };

  // Touch handlers for swipe functionality with velocity detection
  // Disabled on mobile devices
  const isMobile = () => {
    return window.innerWidth <= 768;
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isMobile()) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartTime.current = Date.now();
    touchEndX.current = null;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isMobile()) return;
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (isMobile()) return;
    if (!touchStartX.current || touchEndX.current === null || !touchStartTime.current) {
      touchStartX.current = null;
      touchEndX.current = null;
      touchStartTime.current = null;
      return;
    }

    const distance = touchStartX.current - touchEndX.current;
    const timeElapsed = Date.now() - touchStartTime.current;
    const velocity = Math.abs(distance) / timeElapsed; // pixels per ms

    // Dynamic minimum swipe distance based on velocity
    // Faster swipes require less distance
    const minSwipeDistance = velocity > 0.5 ? 20 : 25; // Very responsive for quick swipes

    // Check if swipe distance is significant enough or if it's a fast swipe
    if (Math.abs(distance) > minSwipeDistance || (Math.abs(distance) > 15 && velocity > 0.3)) {
      if (distance > 0) {
        // Swipe left - next slide
        requestAnimationFrame(() => nextSlide());
      } else {
        // Swipe right - previous slide
        requestAnimationFrame(() => prevSlide());
      }
    }

    // Reset values
    touchStartX.current = null;
    touchEndX.current = null;
    touchStartTime.current = null;
  };

  // Get visible slides (current, previous, next)
  const getVisibleSlides = () => {
    const slides = [];
    for (let i = -1; i <= 1; i++) {
      const index = (currentIndex + i + securityFeatures.length) % securityFeatures.length;
      slides.push({
        ...securityFeatures[index],
        position: i, // -1: left, 0: center, 1: right
      });
    }
    return slides;
  };

  return (
    <section className="security-section">
      <div>
        <div className="security-header">
          <p className="security-subtitle">Unbreakable Security Architecture</p>
          <div className="security-header-content">
            <h2 className="security-title">End-to-End Security</h2>
            <div className="security-nav-top">
              <button className="nav-arrow nav-arrow-left" onClick={prevSlide}>
                <img src="/LandingPageImages/BackArrow.svg" alt="Previous" />
              </button>
              <button className="nav-arrow nav-arrow-right" onClick={nextSlide}>
                <span>Next page</span>
                <img src="/LandingPageImages/NextArrow.svg" alt="Next" />
              </button>
            </div>
          </div>
        </div>

        <div
          className="security-carousel"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="security-carousel-container">
            {getVisibleSlides().map((feature) => (
              <div
                key={`${currentIndex}-${feature.position}`}
                className={`security-card ${feature.position === 0 ? "center-card" : ""}`}
              >
                <div className="security-image-wrapper">
                  <img
                    src={feature.image}
                    alt={feature.title}
                    className={`security-image ${feature.position === 0 ? "center-image" : ""}`}
                  />
                </div>
                <h3 className="security-card-title">{feature.title}</h3>
                <p className="security-card-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SecuritySection;

